import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

type SignedUrlResponse = { url: string };

// GET /api/content/:id/signed-url  →  { url: string }
//
// The signed-URL route returns a 1h Supabase URL and (by default) writes a
// content_views audit row. Pass `log: false` to append `?log=0` and skip that
// insert — used by passive fetches that must NOT count as a view (card
// thumbnails, the admin inline preview, and the partner viewer whose view is
// already logged by POST /api/portal/content/:id/view via useLogContentView).
//
// The log flag is part of the query key so logged and non-logged fetches cache
// separately and a passive thumbnail fetch never suppresses an intended view.
//
// Moved out of features/portal/resources in the file-viewer work so the admin
// content surface can share it.
type SignedUrlOpts = { log?: boolean };

function keyFor(assetId: string, log: boolean) {
  return [...queryKeys.portal.signedAssetUrl(assetId), log ? "log" : "nolog"] as const;
}

function pathFor(assetId: string, log: boolean) {
  return `/content/${assetId}/signed-url${log ? "" : "?log=0"}`;
}

// Declarative, click-driven variant: `enabled: false` until the caller flips
// it true, then awaits a single refetch. staleTime tracks the 1h server TTL
// minus 10 minutes of safety so a cached URL never returns expired.
export function useSignedAssetUrl(
  assetId: string,
  opts: { enabled: boolean } & SignedUrlOpts,
) {
  const log = opts.log ?? true;
  return useQuery<SignedUrlResponse, ApiClientError>({
    queryKey: keyFor(assetId, log),
    queryFn: () => apiFetch<SignedUrlResponse>(pathFor(assetId, log)),
    enabled: opts.enabled,
    staleTime: 1000 * 60 * 50,
    gcTime: 1000 * 60 * 60,
  });
}

// Imperative one-off fetch for callers that want the URL after a click without
// the `enabled`-flag dance.
export function useFetchSignedAssetUrl(opts: SignedUrlOpts = {}) {
  const log = opts.log ?? true;
  const queryClient = useQueryClient();
  return async (assetId: string) => {
    return queryClient.fetchQuery<SignedUrlResponse, ApiClientError>({
      queryKey: keyFor(assetId, log),
      queryFn: () => apiFetch<SignedUrlResponse>(pathFor(assetId, log)),
      staleTime: 1000 * 60 * 50,
    });
  };
}
